import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { X, Minus, Plus, Trash2, CheckCircle2 } from "lucide-react";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import axios from "axios";
import { toast } from "sonner";
import { useCart } from "@/context/CartContext";
import { API, money } from "@/lib/site";

const Input = ({ label, ...props }) => (
  <label className="block">
    <span className="mb-1.5 block font-mono text-[11px] uppercase tracking-[0.2em] text-neutral-400">{label}</span>
    <input
      {...props}
      className="w-full border border-line bg-surface px-4 py-3 text-sm text-neutral-100 outline-none placeholder:text-neutral-600 focus:border-regal-amber transition-colors"
    />
  </label>
);

export default function CartDrawer() {
  const { items, open, setOpen, setQty, removeItem, subtotal, vat, total, clear } = useCart();
  const ccy = items[0]?.currency || "GBP";
  const [step, setStep] = useState("cart");
  const [busy, setBusy] = useState(false);
  const [order, setOrder] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", company: "", country: "", notes: "" });
  const [ppConfig, setPpConfig] = useState(null); // null = not probed, false = not configured
  const [stripeConfig, setStripeConfig] = useState(null); // null = not probed, false = not configured

  useEffect(() => {
    if (!open) return;
    if (ppConfig === null) axios.get(`${API}/paypal/config`).then(({ data }) => setPpConfig(data)).catch(() => setPpConfig(false));
    if (stripeConfig === null) axios.get(`${API}/stripe/config`).then(({ data }) => setStripeConfig(data)).catch(() => setStripeConfig(false));
  }, [open, ppConfig, stripeConfig]);

  const close = () => { setOpen(false); setTimeout(() => { if (order) { setStep("cart"); setOrder(null); } }, 400); };

  const cartItemsPayload = () => items.map((i) => ({ slug: i.slug, qty: i.qty, meta: i.meta || undefined }));
  const formValid = form.name.trim() && /.+@.+\..+/.test(form.email) && form.country.trim();

  const checkout = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/orders`, { customer: form, items: cartItemsPayload() });
      setOrder(data);
      setStep("done");
      clear();
    } catch (err) {
      toast.error("Checkout failed", { description: "Please try again." });
    } finally {
      setBusy(false);
    }
  };

  const stripeCheckout = async () => {
    if (!formValid) {
      toast.error("Details needed", { description: "Name, email and country are required before payment." });
      return;
    }
    try {
      setBusy(true);
      const { data } = await axios.post(`${API}/stripe/create-checkout`, {
        origin_url: window.location.origin,
        customer: form,
        items: cartItemsPayload(),
      });
      window.location.href = data.checkout_url;
    } catch {
      setBusy(false);
      toast.error("Card payment unavailable", { description: "Please try PayPal or place the order without payment." });
    }
  };

  const paypalCreate = async () => {
    const { data } = await axios.post(`${API}/paypal/create-order`, { customer: form, items: cartItemsPayload() });
    return data.paypal_order_id;
  };
  const paypalCapture = async (data) => {
    try {
      setBusy(true);
      const res = await axios.post(`${API}/paypal/capture-order`, { paypal_order_id: data.orderID });
      setOrder(res.data);
      setStep("done");
      clear();
    } catch {
      toast.error("Payment capture failed", { description: "If you were charged, contact us with your PayPal receipt." });
    } finally {
      setBusy(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            key="overlay"
            className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={close}
            data-testid="cart-overlay"
          />
          <motion.aside
            key="drawer"
            data-testid="cart-drawer"
            className="fixed right-0 top-0 z-[70] flex h-full w-full max-w-md flex-col border-l border-line bg-card"
            initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
            transition={{ type: "tween", duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="flex items-center justify-between border-b border-line px-6 py-5">
              <p className="font-display text-sm font-bold uppercase tracking-[0.2em]">
                {step === "cart" ? "Your Cart" : step === "form" ? "Checkout" : "Order Confirmed"}
              </p>
              <button onClick={close} aria-label="Close cart" data-testid="cart-close-button" className="grid h-10 w-10 place-items-center border border-line text-neutral-300 hover:border-regal-amber hover:text-regal-amber transition-colors">
                <X size={18} />
              </button>
            </div>

            {step === "cart" && (
              <>
                <div className="flex-1 overflow-y-auto px-6 py-5">
                  {items.length === 0 ? (
                    <div className="flex h-full flex-col items-center justify-center gap-4 text-center">
                      <p className="text-neutral-400 text-sm">Your cart is empty.</p>
                      <Link to="/tuning-tools" onClick={close} data-testid="cart-browse-tools-link" className="border border-regal-amber px-6 py-3 font-display text-xs font-bold uppercase tracking-[0.16em] text-regal-amber hover:bg-regal-amber hover:text-void transition-colors">
                        Browse Tuning Tools
                      </Link>
                    </div>
                  ) : (
                    <ul className="space-y-5">
                      {items.map((i) => (
                        <li key={i.key} className="flex gap-4 border border-line bg-surface p-3" data-testid={`cart-item-${i.slug}`}>
                          <img src={i.image} alt={i.name} className="h-20 w-20 shrink-0 object-cover" loading="lazy" />
                          <div className="flex min-w-0 flex-1 flex-col justify-between py-0.5">
                            <div className="flex items-start justify-between gap-2">
                              <div className="min-w-0">
                                <p className="text-sm font-semibold leading-snug">{i.name}</p>
                                {i.meta?.summary && <p className="mt-1 whitespace-pre-line text-[11px] leading-relaxed text-neutral-500" data-testid={`cart-item-config-${i.slug}`}>{i.meta.summary}</p>}
                              </div>
                              <button onClick={() => removeItem(i.key)} aria-label="Remove item" data-testid={`cart-remove-${i.slug}`} className="shrink-0 text-neutral-500 hover:text-regal-red transition-colors"><Trash2 size={15} /></button>
                            </div>
                            <div className="flex items-center justify-between">
                              <div className="flex items-center border border-line">
                                <button onClick={() => setQty(i.key, i.qty - 1)} data-testid={`cart-minus-${i.slug}`} className="grid h-8 w-8 place-items-center text-neutral-300 hover:text-regal-amber"><Minus size={13} /></button>
                                <span className="w-8 text-center font-mono text-xs">{i.qty}</span>
                                <button onClick={() => setQty(i.key, i.qty + 1)} data-testid={`cart-plus-${i.slug}`} className="grid h-8 w-8 place-items-center text-neutral-300 hover:text-regal-amber"><Plus size={13} /></button>
                              </div>
                              <p className="font-mono text-sm text-regal-amber">{money(i.price * i.qty, i.currency)}</p>
                            </div>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                {items.length > 0 && (
                  <div className="border-t border-line px-6 py-5">
                    <div className="space-y-1.5 font-mono text-xs text-neutral-400">
                      <div className="flex justify-between"><span>Subtotal</span><span data-testid="cart-subtotal">{money(subtotal, ccy)}</span></div>
                      <div className="flex justify-between"><span>Shipping</span><span data-testid="cart-shipping">{items.some((i) => i.physical !== false) ? "Confirmed after order" : "Free — digital delivery"}</span></div>
                      <div className="flex justify-between text-sm text-ink"><span>Total</span><span className="text-regal-amber" data-testid="cart-total">{money(total, ccy)}</span></div>
                    </div>
                    <button onClick={() => setStep("form")} data-testid="cart-checkout-button" className="mt-5 w-full bg-regal-amber py-4 font-display text-sm font-bold uppercase tracking-[0.16em] text-void hover:bg-amber-400 transition-colors">
                      Proceed to Checkout
                    </button>
                    <p className="mt-3 text-center font-mono text-[10px] uppercase tracking-[0.18em] text-neutral-500">
                      {ppConfig ? "Secure checkout — PayPal available at the next step" : "Demo checkout — no payment taken"}
                    </p>
                  </div>
                )}
              </>
            )}

            {step === "form" && (
              <form onSubmit={checkout} className="flex flex-1 flex-col overflow-y-auto px-6 py-5" data-testid="checkout-form">
                <div className="space-y-4">
                  <Input label="Full name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="checkout-name-input" />
                  <Input label="Email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="checkout-email-input" />
                  <Input label="Company (optional)" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} data-testid="checkout-company-input" />
                  <Input label="Country" required value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} data-testid="checkout-country-input" />
                  <label className="block">
                    <span className="mb-1.5 block font-mono text-[11px] uppercase tracking-[0.2em] text-neutral-400">Notes (optional)</span>
                    <textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="checkout-notes-input" className="w-full border border-line bg-surface px-4 py-3 text-sm text-neutral-100 outline-none focus:border-regal-amber transition-colors" />
                  </label>
                </div>
                <div className="mt-auto pt-6 space-y-3">
                  {(ppConfig || stripeConfig) ? (
                    <div data-testid="payment-methods">
                      {ppConfig && (
                        <div data-testid="paypal-buttons">
                          <PayPalScriptProvider options={{ clientId: ppConfig.clientId, currency: ccy, intent: "capture" }}>
                            <PayPalButtons
                              style={{ layout: "vertical", color: "gold", shape: "rect", label: "paypal", height: 48 }}
                              forceReRender={[total, ccy]}
                              onClick={(data, actions) => {
                                if (!formValid) {
                                  toast.error("Details needed", { description: "Name, email and country are required before payment." });
                                  return actions.reject();
                                }
                                return actions.resolve();
                              }}
                              createOrder={paypalCreate}
                              onApprove={paypalCapture}
                              onError={() => toast.error("PayPal error", { description: "Payment could not be started. Please try again." })}
                            />
                          </PayPalScriptProvider>
                          {ppConfig.env !== "live" && (
                            <p className="mt-2 text-center font-mono text-[10px] uppercase tracking-[0.18em] text-neutral-500" data-testid="paypal-sandbox-note">PayPal sandbox — test payments only</p>
                          )}
                        </div>
                      )}
                      {stripeConfig && (
                        <button type="button" onClick={stripeCheckout} disabled={busy} data-testid="stripe-pay-button"
                          className={`${ppConfig ? "mt-2 " : ""}w-full bg-regal-amber py-4 font-display text-sm font-bold uppercase tracking-[0.16em] text-void hover:bg-amber-400 transition-colors disabled:opacity-60`}>
                          {busy ? "Starting secure payment…" : `Pay by Card — ${money(total, ccy)}`}
                        </button>
                      )}
                      {stripeConfig && stripeConfig.mode !== "live" && (
                        <p className="mt-2 text-center font-mono text-[10px] uppercase tracking-[0.18em] text-neutral-500" data-testid="stripe-test-note">Card payments in test mode — no real charge</p>
                      )}
                      <button type="submit" disabled={busy} data-testid="checkout-submit-button" className="mt-2 w-full border border-line py-3 font-display text-[11px] font-bold uppercase tracking-[0.14em] text-neutral-400 hover:border-regal-amber hover:text-regal-amber transition-colors disabled:opacity-60">
                        {busy ? "Placing order…" : "Pay later — place order without payment"}
                      </button>
                    </div>
                  ) : (
                    <button type="submit" disabled={busy} data-testid="checkout-submit-button" className="w-full bg-regal-amber py-4 font-display text-sm font-bold uppercase tracking-[0.16em] text-void hover:bg-amber-400 transition-colors disabled:opacity-60">
                      {busy ? "Placing order…" : `Place Demo Order — ${money(total, ccy)}`}
                    </button>
                  )}
                  <button type="button" onClick={() => setStep("cart")} data-testid="checkout-back-button" className="w-full border border-line py-3.5 font-display text-xs font-bold uppercase tracking-[0.16em] text-neutral-300 hover:border-regal-amber hover:text-regal-amber transition-colors">
                    Back to cart
                  </button>
                </div>
              </form>
            )}

            {step === "done" && order && (
              <div className="flex flex-1 flex-col items-center justify-center gap-5 px-8 text-center" data-testid="checkout-success">
                <CheckCircle2 size={52} className="text-regal-amber" />
                <div>
                  <p className="font-display text-xl font-bold uppercase tracking-wide">Order placed</p>
                  <p className="mt-2 text-sm text-neutral-400">Reference <span className="font-mono text-regal-amber" data-testid="order-reference">{order.reference}</span></p>
                  <p className="mt-1 text-sm text-neutral-400">Total {money(order.total_inc_vat, order.items?.[0]?.currency || "GBP")}</p>
                  <p className="mt-4 font-mono text-[11px] uppercase tracking-[0.2em] text-neutral-500">
                    {order.payment === "paypal" ? "Paid via PayPal — confirmation emailed" : order.payment === "stripe" ? "Paid by card — confirmation emailed" : "Demo order — payment was simulated"}
                  </p>
                </div>
                <button onClick={close} data-testid="checkout-done-button" className="border border-regal-amber px-8 py-3.5 font-display text-xs font-bold uppercase tracking-[0.16em] text-regal-amber hover:bg-regal-amber hover:text-void transition-colors">
                  Continue
                </button>
              </div>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
